import { isPresent, isObject } from '@apl-digital/utils';
import { deepEqual } from 'fast-equals';
import { z } from 'zod';

const literalSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null()
]);
const jsonSchema = z.lazy(()=>z.union([
        literalSchema,
        z.array(jsonSchema),
        z.record(jsonSchema)
    ]));

const gatherZodParseErrors = (error)=>{
    const errors = {};
    error.error.issues.forEach((e)=>{
        errors[e.path.join('.')] = e;
    });
    return errors;
};

const printZodParseErrors = (errors, pathPrefix)=>{
    console.groupCollapsed('[config.yaml] ❌ %c%s', 'color: teal', pathPrefix);
    for (const [errorPath, error] of Object.entries(errors)){
        const path = [
            pathPrefix,
            errorPath
        ].join('.');
        switch(error.code){
            case 'invalid_type':
            case 'invalid_literal':
                console.log(`[config.yaml] %c${path}%c: Expected %o, but got %o`, 'color: teal', 'color: notset', error.expected, error.received);
                break;
            case 'unrecognized_keys':
                console.log(`[config.yaml] %c${path}%c: Unrecognized keys: %o`, 'color: teal', 'color: notset', error.keys);
                break;
            default:
                console.log(`[config.yaml] %c${path}%c: ${error.message}`, 'color: teal', 'color: notset');
        }
    }
    console.groupEnd();
};
const printParseSiteConfigsResults = (results)=>{
    if (!results.success) {
        console.groupCollapsed('[config.yaml] ❌ %csiteConfigs', 'color: teal');
        if (results.type === 'general') {
            console.log('[config.yaml] %s', results.error);
        } else if (results.type === 'json-parse') {
            console.groupCollapsed('[config.yaml] JSON parse errors');
            printZodParseErrors(results.errors, 'siteConfigs');
            console.groupEnd();
        } else if (results.type === 'invalid-type') {
            console.log('[config.yaml] %csiteConfigs%c: Expected %s, but got %s', 'color: teal', 'color: notset', results.expected, results.received);
        }
        console.groupEnd();
    } else {
        console.log('[config.yaml] ✅ %csiteConfigs', 'color: teal');
    }
};
const printParseConfigResults = (results)=>{
    if (!results.success) {
        printZodParseErrors(results.errors, results.configPath);
    } else {
        console.log('[config.yaml] ✅ %c%s', 'color: teal', results.configPath);
    }
};
const printValidationResults = (results)=>{
    if (results.success) {
        console.groupCollapsed('[config.yaml] %cConfig validation successful', 'color: green');
        console.info('[config.yaml] Found %d applicable configs', results.applicableConfigs.length);
        var _results_chosenConfigPath;
        console.log('[config.yaml] Will load config: %c%s', 'color: teal', (_results_chosenConfigPath = results.chosenConfigPath) != null ? _results_chosenConfigPath : 'null');
        for (const parsedConfig of Object.values(results.parsedConfigs)){
            printParseConfigResults(parsedConfig);
        }
        console.groupEnd();
    } else {
        console.groupCollapsed('[config.yaml] %cConfig validation failed', 'color: red');
        if (results.type === 'general') {
            console.log('[config.yaml] %s', results.error);
        } else if (results.type === 'siteConfigs-parse') {
            printParseSiteConfigsResults(results.siteConfigs);
        } else if (results.type === 'config-parse') {
            console.groupCollapsed('[config.yaml] Found %d applicable configs', results.applicableConfigs.length);
            console.table(results.applicableConfigs.map((path)=>({
                    Location: path
                })), [
                'Location'
            ]);
            console.groupEnd();
            if (isPresent(results.chosenConfigPath)) {
                console.log('[config.yaml] Will load config: %c%s', 'color: teal', results.chosenConfigPath);
            } else {
                console.log("[config.yaml] Won't load any config!");
            }
            for (const parsedConfig of Object.values(results.parsedConfigs)){
                printParseConfigResults(parsedConfig);
            }
        }
        console.groupEnd();
    }
};

const getConfigLocations = (node)=>{
    function* helper(currentNode, prev = []) {
        for (const [key, value] of Object.entries(currentNode)){
            if (key === 'config') {
                yield prev;
            } else if (isPresent(value)) {
                yield* helper(value, prev.concat(key));
            }
        }
    }
    const results = [];
    for (const i of helper(node)){
        results.push(i);
    }
    return results;
};
const findApplicableConfigs = (configLocations, requestedConfigLocation)=>{
    function* helper(path) {
        for (const configLocation of configLocations){
            if (deepEqual(configLocation, path)) {
                yield configLocation;
            }
        }
        if (path.length > 0) {
            yield* helper(path.slice(0, -1));
        }
    }
    const results = [];
    for (const i of helper(requestedConfigLocation)){
        results.push(i);
    }
    return results;
};
const delve = (node, path)=>{
    let current = node;
    for (const key of path){
        if (key in current) {
            current = current[key];
        } else {
            break;
        }
    }
    return current;
};
const parseSiteConfigs = (rawConfig)=>{
    const parsedJson = jsonSchema.safeParse(rawConfig);
    if (!parsedJson.success) {
        return {
            success: false,
            type: 'json-parse',
            errors: gatherZodParseErrors(parsedJson)
        };
    }
    const data = parsedJson.data;
    if (isObject(data) && 'siteConfigs' in data) {
        if (!isObject(data['siteConfigs'])) {
            return {
                success: false,
                type: 'invalid-type',
                expected: 'object',
                received: data['siteConfigs']
            };
        }
        return {
            success: true,
            data: data['siteConfigs']
        };
    }
    return {
        success: false,
        type: 'general',
        error: 'Could not find siteConfigs in config.yaml'
    };
};
const parseConfig = (schema, config, pathPrefix)=>{
    const parsedSiteConfig = schema.safeParse(config);
    if (!parsedSiteConfig.success) {
        return {
            success: false,
            configPath: pathPrefix.join('.'),
            errors: gatherZodParseErrors(parsedSiteConfig)
        };
    }
    return {
        success: true,
        configPath: pathPrefix.join('.'),
        config: parsedSiteConfig.data
    };
};
const getCurrentDomainLevels = ()=>{
    const hostname = window.location.hostname;
    return hostname.split('.').reverse();
};
const pickConfig = (configLocations)=>{
    // Choose the first applicable config, because its the most specific match
    const configPath = configLocations.at(0);
    if (!isPresent(configPath)) {
        return null;
    }
    return configPath.concat('config');
};
const loadCurrentConfig = (schema, rawConfig, domainLevels)=>{
    const parsedSiteConfigs = parseSiteConfigs(rawConfig);
    if (!parsedSiteConfigs.success) {
        printParseSiteConfigsResults(parsedSiteConfigs);
        throw new Error('[config.yaml] Failed to parse siteConfigs');
    }
    const configLocations = getConfigLocations(parsedSiteConfigs.data);
    if (configLocations.length === 0) {
        throw new Error('[config.yaml] No config locations found, please check siteConfigs in config.yaml');
    }
    const applicableConfigs = findApplicableConfigs(configLocations, domainLevels);
    if (applicableConfigs.length === 0) {
        throw new Error(`[config.yaml] No applicable configs found for ${domainLevels.join('.')}, please check siteConfigs in config.yaml`);
    }
    const configPath = pickConfig(applicableConfigs);
    if (!isPresent(configPath)) {
        throw new Error('[config.yaml] Failed to pick a config from applicable configs, please check siteConfigs in config.yaml');
    }
    const rawSiteConfig = delve(parsedSiteConfigs.data, configPath);
    const parsedConfig = parseConfig(schema, rawSiteConfig, configPath);
    if (!parsedConfig.success) {
        printParseConfigResults(parsedConfig);
        throw new Error('Failed to parse config.yaml');
    }
    return parsedConfig.config;
};

const validateConfigs = (schema, rawConfig, domainLevels)=>{
    const parsedSiteConfigs = parseSiteConfigs(rawConfig);
    if (!parsedSiteConfigs.success) {
        return {
            success: false,
            type: 'siteConfigs-parse',
            siteConfigs: parsedSiteConfigs
        };
    }
    const configLocations = getConfigLocations(parsedSiteConfigs.data);
    if (configLocations.length === 0) {
        return {
            success: false,
            type: 'general',
            error: 'No config locations found, please check siteConfigs in config.yaml'
        };
    }
    const applicableConfigs = findApplicableConfigs(configLocations, domainLevels);
    const chosenConfigPath = pickConfig(applicableConfigs);
    const parsedConfigs = {};
    for (const configLocation of configLocations){
        const configPath = configLocation.concat('config');
        const rawSiteConfig = delve(parsedSiteConfigs.data, configPath);
        const parsedConfig = parseConfig(schema, rawSiteConfig, configPath);
        parsedConfigs[configPath.join('.')] = parsedConfig;
    }
    if (Object.entries(parsedConfigs).some(([, r])=>!r.success)) {
        var _chosenConfigPath_join;
        return {
            success: false,
            type: 'config-parse',
            applicableConfigs: applicableConfigs.map((c)=>c.concat('config').join('.')),
            chosenConfigPath: (_chosenConfigPath_join = chosenConfigPath == null ? void 0 : chosenConfigPath.join('.')) != null ? _chosenConfigPath_join : null,
            parsedConfigs: parsedConfigs
        };
    }
    var _chosenConfigPath_join1;
    return {
        success: true,
        applicableConfigs: applicableConfigs.map((c)=>c.concat('config').join('.')),
        chosenConfigPath: (_chosenConfigPath_join1 = chosenConfigPath == null ? void 0 : chosenConfigPath.join('.')) != null ? _chosenConfigPath_join1 : null,
        parsedConfigs: parsedConfigs
    };
};

export { delve, findApplicableConfigs, getConfigLocations, getCurrentDomainLevels, loadCurrentConfig, parseConfig, parseSiteConfigs, pickConfig, printParseConfigResults, printParseSiteConfigsResults, printValidationResults, validateConfigs };
